import {
    useEffect,
    useRef
} from 'react'
import { useVirtualizer } from '@tanstack/react-virtual'
import { useInfiniteQuery } from '@tanstack/react-query'

import List from '@mui/material/List'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'

import axios from '@shared/utils/axios'

export const useInfiniteList = ({
    key,
    endpoint,
    params,
    enabled=true
}) => {
    const query = useInfiniteQuery({
        queryKey: key,
        queryFn: async ({ pageParam }) => {
            const request = await axios.get(endpoint, {
                params: {
                    ...(params || {}),
                    cursor: pageParam,
                }
            })
            return request.data
        },
        getNextPageParam: (lastPage) => !lastPage.next
            ? undefined
            : new URL(lastPage.next).searchParams.get('cursor'),
        enabled
    })
    const data = query.data?.pages.flatMap(p => p.results) ?? []

    // virtualization
    const scroller = useRef(null)
    const virtualizer = useVirtualizer({
        count: data.length,
        getScrollElement: () => scroller.current,
        estimateSize: () => 72,
        overscan: 20
    })

    useEffect(() => {
        const items = virtualizer.getVirtualItems()
        if (!items.length) return
        const lastItem = items[items.length - 1]
        if (
            lastItem.index >= data.length - 10 &&
            query.hasNextPage &&
            !query.isFetchingNextPage
        ) {
            query.fetchNextPage()
        }
    }, [
        virtualizer.getVirtualItems(),
        data.length,
        query.hasNextPage,
        query.isFetchingNextPage,
    ])

    return {
        isLoading: (query.isFetching && !query.isFetchingNextPage),
        error: query.error,
        data,
        virtualizer,
        scroller,
        hasNextPage: query.hasNextPage,
        visibleItems: virtualizer.getVirtualItems().map(vItem => ({item: data[vItem.index], options: vItem}))
    }
}

export function InfiniteList({
    manager,
    additionHeight=0,
    children
}) {
    return (
        <Box
            ref={manager.scroller}
            sx={{
                height: '100%',
                overflow: 'auto',
            }}
        >
            <List
                sx={{
                    height: manager.virtualizer.getTotalSize()+additionHeight,
                    position: 'relative',
                }}
            >
                {children}
            </List>
            {manager.hasNextPage &&
                <Box sx={{
                    width: '100%',
                    height: 'auto'
                }} className='centerized'>
                    <CircularProgress />
                </Box>
            }
        </Box>
    )
}